import { Link, useParams } from "react-router-dom"
import { ArrowLeft, UserRoundPlus } from "lucide-react"
import { useProject } from "../state/useProject"
import { useApp } from "../state/AppState"
import { Card, Spinner, cx } from "../components/ui"
import { PersonGrid, ProjectTeams } from "../components/issues/ProjectTeams"
import { NotFoundPage } from "./NotFoundPage"

/**
 * Who is on this project.
 *
 * Two lists, and the difference between them matters: a team is on the whole
 * project, a guest was invited to one issue. Showing them together without
 * saying which is which is how someone ends up assuming a person can see
 * something they cannot.
 */
export function ProjectTeamPage() {
	const { projectId } = useParams()
	const { project, teams, people, loading, error, reload } = useProject(projectId)
	const { user } = useApp()

	if (error?.status === 404) return <NotFoundPage />
	if (loading && !project) return <Spinner label="Loading the team" />
	if (!project) return null

	const teamMemberIds = new Set(
		teams.flatMap((team) => team.members.map((member) => member.user_id))
	)
	const guests = people.filter(
		(person) =>
			!teamMemberIds.has(person.user_id) && person.user_id !== project.owner_id
	)

	return (
		<div className="space-y-6">
			<header>
				<Link
					to={`/projects/${projectId}`}
					className="inline-flex items-center gap-1.5 text-[0.8125rem] font-medium text-ink-500 transition-colors hover:text-ink-900"
				>
					<ArrowLeft size={14} aria-hidden />
					{project.name}
				</Link>
				<h1 className="mt-1.5 text-xl font-semibold tracking-tight text-ink-900">
					Team
				</h1>
				<p className="mt-0.5 max-w-2xl text-[0.8125rem] leading-relaxed text-ink-500">
					Teams see the whole project. Everyone else sees only the questions they
					were named on.
				</p>
			</header>

			<ProjectTeams project={project} teams={teams} onChanged={() => reload({ quiet: true })} />

			{guests.length > 0 && (
				<section className="space-y-3">
					<div>
						<h2 className="flex items-center gap-2 text-[0.9375rem] font-semibold text-ink-900">
							<UserRoundPlus size={16} className="text-ink-400" aria-hidden />
							Added to single questions
						</h2>
						<p className="mt-0.5 text-[0.8125rem] leading-relaxed text-ink-500">
							These people were added to single questions. They see those and
							nothing else here.
						</p>
					</div>
					<Card className="overflow-hidden">
						<PersonGrid members={guests} currentUserId={user?.user_id} />
					</Card>
				</section>
			)}
		</div>
	)
}

/**
 * The strip at the top of the issue list: who is on the project, at a glance,
 * and the way in to change it.
 */
export function TeamStrip({ projectId, project, teams }) {
	const members = []
	const seen = new Set()
	teams.forEach((team) => {
		team.members.forEach((member) => {
			if (seen.has(member.user_id)) return
			seen.add(member.user_id)
			members.push(member)
		})
	})

	if (members.length === 0 && !project?.is_owner) return null

	return (
		<Link
			to={`/projects/${projectId}/team`}
			className={cx(
				"inline-flex items-center gap-2 rounded-full border border-ink-200 bg-white",
				"py-1 pl-1 pr-3 text-2xs text-ink-600 shadow-card transition-colors",
				"hover:border-ink-300 hover:text-ink-900"
			)}
		>
			{members.length > 0 ? (
				<>
					<span className="flex -space-x-1.5">
						{members.slice(0, 4).map((member) => (
							<Initials key={member.user_id} person={member} />
						))}
					</span>
					<span>
						{members.length} on this project
						{teams.length > 1 ? ` · ${teams.length} teams` : ""}
					</span>
				</>
			) : (
				<>
					<span className="grid h-6 w-6 place-items-center rounded-full bg-brand-50 text-brand-700">
						<UserRoundPlus size={12} aria-hidden />
					</span>
					<span>Invite a team</span>
				</>
			)}
		</Link>
	)
}

function Initials({ person }) {
	return (
		<span
			title={[person.name, person.role, person.company].filter(Boolean).join(" · ")}
			className="grid h-6 w-6 place-items-center rounded-full bg-ink-100 text-[0.625rem] font-semibold text-ink-700 ring-2 ring-white"
		>
			{person.initials || person.name?.slice(0, 1) || "?"}
		</span>
	)
}
